import axios from 'axios'
import { ReactElement, useMemo, useState } from 'react'
import { Button } from '@calabriocloud/beacon-ui'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { actionKey, ContactGoalConfig, statusKey } from './constants'
import { Switch } from '@calabriocloud/beacon-ui'
import { useHistory } from 'react-router-dom'
import { AgGridReact } from 'ag-grid-react'
import { ICellRendererParams } from 'ag-grid-community'

function getMode(status: number): 'edit' | 'view' {
  return status === statusKey.indexOf('editable') ? 'edit' : 'view'
}

function ButtonCellRender({ data }: ICellRendererParams) {
  const { t } = useTranslation()
  const history = useHistory()

  const { status, goalId } = data

  const viewContactGoalPage = (goal: ContactGoalConfig) => {
    const { goalId, status } = goal
    history.push('/admin/contactGoal/' + goalId + '/' + getMode(status))
  }
  const cloneContactGoalPage = (goal: ContactGoalConfig) => {
    const { goalId } = goal
    history.push('/admin/contactGoal/' + goalId + '/clone')
  }

  return (
    <div className='flex justify-end gap-4 pr-2'>
      <div>
        <Button variant="secondary" className="rounded-[4px] px-2 py-1 w-16" data-testid={`clone-contact-goal-${goalId}`} onPress={() => cloneContactGoalPage(data)}>{t('Clone')}</Button>
      </div>
      <div>
        <Button variant="secondary" className="rounded-[4px] px-2 py-1 w-16" data-testid={`view-contact-goal-${goalId}`} onPress={() => viewContactGoalPage(data)}>
          {t(getMode(status))}
        </Button>
      </div>
    </div>
  )
}
interface ContactGoalTableProps {
  items?: ContactGoalConfig[],
  onDisableFilter: (enable: boolean) => void,
}

function ContactGoalTable({ items, onDisableFilter }: ContactGoalTableProps): ReactElement {
  const { t } = useTranslation()
  const columnDefs = useMemo(() => ([
    {
      headerName: t('goalIdLabelv2'),
      field: 'goalId',
      suppressMenu: false,
      filter: true,
    },
    {
      headerName: t('name'),
      field: 'name',
      suppressMenu: false,
      filter: true,
    },
    {
      headerName: t('status'),
      field: 'status',
      cellRenderer: ({ data }: ICellRendererParams): string => (t(statusKey[data.status] || '')),
    },
    {
      headerName: t('action'),
      field: 'contactGoalActionId',
      cellRenderer: ({ data }: ICellRendererParams) => (t(actionKey[data.contactGoalActionId] || '')),
    },
    {
      headerComponent: () => (
        <div className='flex justify-end'>
          <div>
            <Switch data-test-id='show-inactive-goal-switch' onChange={onDisableFilter}>
              {t('showInactiveGoals')}
            </Switch>
          </div>
        </div>
      ),
      cellRenderer: ButtonCellRender,
    },
  ]), [t])

  return (
    <div className="ag-theme-material w-full h-[60vh] text-sm text-gray-700" style={{ borderRadius: '8px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
      <AgGridReact
        rowData={items}
        columnDefs={columnDefs}
        defaultColDef={{
          flex: 1,
          minWidth: 150,
          resizable: true,
          sortable: true,
          suppressMenu: true,
          lockPinned: true,
          headerClass: 'text-sm text-gray-700 font-semibold bg-gray-50',
        }}
        suppressMenuHide={true}
        animateRows={false}
        // TODO: Use a spinner component for loading state
        loadingOverlayComponent={() => <div className="ag-overlay-loading-center">{t('loading')}</div>}
        noRowsOverlayComponent={() => <div className="ag-overlay-no-rows-center">{t('contactQueueAdminNoGoalsToShow')}</div>}
      />
    </div>
  )

}

export function ContactGoalAdmin(): ReactElement {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery<ContactGoalConfig[]>({
    queryKey: ['ContactGoalConfigs'],
    queryFn: async () => {
      const res = await axios.get<Omit<ContactGoalConfig, 'id'>[]>('api/rest/recording/contactGoal')
      return res.data.map(item => ({ id: item.goalId, ...item }))
    },
  })

  const [disableFilter, setDisabledFilter] = useState(false)

  const sortedData = useMemo(() => {
    // If data is loaded but it is still undefined set it to an empty array to indicate no data
    if (!isLoading && !data) {
      return []
    }

    if (!disableFilter) {
      return data?.filter(item => item.status !== 2)
    }
    return data
  }, [isLoading, disableFilter, data])

  const history = useHistory()
  const newContactGoalPage = () => {
    history.push('/admin/contactGoal/create')
  }

  return (
    <div className='bg-white flex flex-col h-full'>
      <div className='px-[54px] py-[32px] flex justify-between border-b border-black bg-white sticky top-0 z-20' >
        <h1 className="text-3xl font-bold">{t('contactGoalAdministrationv2')}</h1>
        <Button data-testid='create-new-contact-queue-goal' onPress={newContactGoalPage}>{t('newGoal')}</Button>
      </div>
      <br />
      <div className='px-[54px] py-[32px]'>
        <ContactGoalTable
          items={sortedData}
          onDisableFilter={setDisabledFilter}
        />
      </div>
    </div>
  )
}
